/**
 * usePositionLevels Hook (V86)
 *
 * httpsCallable을 통해 직위(Position Level) 목록을 조회하는 Hook입니다.
 *
 * **V86 변경:**
 * - Firestore 직접 접근 → httpsCallable 방식으로 전환
 * - 보안 규칙 위반 문제 해결
 *
 * **기능:**
 * - 백엔드 getPositionLevels callable 함수 호출
 * - 검색/필터 기능
 * - 로딩/에러 상태 관리
 * - refetch 함수 제공 (수동 새로고침)
 *
 * **사용 예시:**
 * ```tsx
 * const { positionLevels, isLoading, error, refetch } = usePositionLevels({
 *   searchText: '과장',
 *   status: 'active'
 * });
 * ```
 */

import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import { PositionLevel, PositionLevelListFilter } from '../types/positionLevel';
import { getCached, setCached, CACHE_KEY } from '../utils/masterDataCache';

/**
 * usePositionLevels Hook 반환 타입
 */
export interface UsePositionLevelsResult {
  /** 직위 목록 */
  positionLevels: PositionLevel[];

  /** 로딩 중 여부 */
  isLoading: boolean;

  /** 에러 */
  error: Error | null;

  /** 수동 새로고침 함수 */
  refetch: () => void;
}

/**
 * 직위 목록 조회 Hook
 *
 * Firestore의 `positionLevels` 컬렉션에서 직위 목록을 조회하고,
 * 필터 조건에 맞게 클라이언트 단에서 필터링합니다.
 *
 * **정렬:**
 * - Firestore에서 order 필드로 오름차순 정렬하여 조회
 * - 정렬 순서가 작은 값부터 표시 (화면 표시 순서)
 *
 * **필터링:**
 * - searchText: 직위명/코드에 포함되는지 체크 (대소문자 무시)
 * - status: 'all'이 아니면 정확히 일치하는 상태만
 *
 * @param filters 검색/필터 조건
 * @returns UsePositionLevelsResult
 */
export function usePositionLevels(
  filters: PositionLevelListFilter = {}
): UsePositionLevelsResult {
  const [positionLevels, setPositionLevels] = useState<PositionLevel[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [refreshKey, setRefreshKey] = useState<number>(0);

  // refetch 함수: refreshKey를 변경하여 useEffect 재실행
  const refetch = () => {
    setRefreshKey(prev => prev + 1);
  };

  useEffect(() => {
    // Firebase가 초기화되지 않은 경우 (DEMO 모드)
    if (!functions) {
      setPositionLevels([]);
      setIsLoading(false);
      return;
    }

    // V86: httpsCallable을 통한 직위 목록 조회
    const fetchPositionLevels = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // V259: 캐시 키 — status + searchText 조합
        const cacheKey = `${CACHE_KEY.POSITION_LEVELS}${filters.status || 'all'}:${filters.searchText || ''}`;
        const cached = getCached<PositionLevel[]>(cacheKey);

        if (cached) {
          setPositionLevels(cached);
          setIsLoading(false);
          return;
        }

        const getPositionLevelsFn = httpsCallable<
          { status?: string; searchText?: string },
          { success: boolean; positionLevels: PositionLevel[] }
        >(functions!, 'getPositionLevels');

        const result = await getPositionLevelsFn({
          status: filters.status,
          searchText: filters.searchText,
        });

        if (result.data.success) {
          setPositionLevels(result.data.positionLevels);
          // V259: 캐시 저장
          setCached(cacheKey, result.data.positionLevels);
        } else {
          throw new Error('직위 목록 조회 실패');
        }

        setIsLoading(false);
      } catch (err) {
        console.error('직위 목록 조회 실패:', err);
        setError(err as Error);
        setIsLoading(false);
      }
    };

    fetchPositionLevels();
  }, [filters.searchText, filters.status, refreshKey]);

  return { positionLevels, isLoading, error, refetch };
}

/**
 * V86: 필터링은 이제 백엔드에서 처리됩니다.
 * 이전 filterPositionLevels 함수는 제거됨.
 */

/**
 * ============================================
 * usePositionLevel Hook (단건 조회)
 * ============================================
 */

/**
 * usePositionLevel Hook 반환 타입
 */
export interface UsePositionLevelResult {
  /** 직위 정보 */
  positionLevel: PositionLevel | null;

  /** 로딩 중 여부 */
  isLoading: boolean;

  /** 에러 */
  error: Error | null;

  /** 수동 새로고침 함수 */
  refetch: () => void;
}

/**
 * 단일 직위 조회 Hook
 *
 * Firestore의 `positionLevels` 컬렉션에서 특정 ID의 직위 정보를 조회합니다.
 *
 * **사용 예시:**
 * ```tsx
 * const { positionLevel, isLoading, error } = usePositionLevel('pos_staff');
 * ```
 *
 * @param positionLevelId 직위 ID (Firestore 문서 ID)
 * @returns UsePositionLevelResult
 */
export function usePositionLevel(
  positionLevelId: string | undefined
): UsePositionLevelResult {
  // V86: 전체 목록에서 찾는 방식으로 변경 (별도 단건 API 불필요)
  const { positionLevels, isLoading, error, refetch } = usePositionLevels();
  const [positionLevel, setPositionLevel] = useState<PositionLevel | null>(null);

  useEffect(() => {
    if (!positionLevelId) {
      setPositionLevel(null);
      return;
    }

    const found = positionLevels.find((pl) => pl.id === positionLevelId);
    setPositionLevel(found || null);
  }, [positionLevelId, positionLevels]);

  return { positionLevel, isLoading, error, refetch };
}

/**
 * ============================================
 * TODO: 성능 최적화 (향후)
 * ============================================
 *
 * 현재는 클라이언트 단에서 전체 목록을 가져와 필터링합니다.
 * 직위 마스터는 데이터가 많지 않으므로 (10~20개 수준) 이 방식으로 충분합니다.
 *
 * 만약 직위 종류가 매우 많아진다면 (예: 50개 이상):
 *
 * 1. **서버 사이드 필터링:**
 *    - Firestore Query where 조건 사용
 *    - 예: where('status', '==', 'active')
 *
 * 2. **캐싱:**
 *    - React Query 또는 SWR 도입
 *    - 동일한 쿼리에 대해 캐시 활용
 *    - 직위 마스터는 변경 빈도가 낮으므로 캐싱 효과가 큼
 *
 * 3. **실시간 구독:**
 *    - onSnapshot을 사용하여 실시간 동기화
 *    - 직위 마스터는 변경 빈도가 낮으므로 getDocs 방식 유지 권장
 */

/**
 * ============================================
 * 직위 정렬 순서 (order vs rankWeight)
 * ============================================
 *
 * **order (정렬 순서):**
 * - 화면 표시 순서를 결정
 * - 작은 값부터 화면에 표시 (오름차순)
 * - 예: 사원(1), 대리(2), 과장(3), ..., 대표(8)
 * - 용도: 직위 선택 드롭다운, 직위 목록 화면 등
 *
 * **rankWeight (위계 가중치):**
 * - 직위의 높낮이를 나타냄
 * - 큰 값일수록 높은 직위 (내림차순 정렬 시 사용)
 * - 예: 대표(8), 이사(7), ..., 대리(2), 사원(1)
 * - 용도: 조직도에서 직위별 정렬, 승인 권한 체크 등
 *
 * **왜 두 가지가 필요한가?**
 *
 * - order만 있으면:
 *   - 화면 표시 순서는 제어 가능
 *   - 하지만 "높은 직위 우선 정렬"을 위해 order를 역순으로 사용해야 함 (혼란)
 *
 * - rankWeight만 있으면:
 *   - 높은 직위 우선 정렬은 가능
 *   - 하지만 화면 표시 순서를 "사원 → 대표" 순으로 하려면 rankWeight를 역순으로 정렬해야 함 (비직관적)
 *
 * - 둘 다 있으면:
 *   - order: 화면 표시 순서 (사원 1 → 대표 8)
 *   - rankWeight: 위계 비교 (대표 8 > 사원 1)
 *   - 각각의 목적에 맞게 명확하게 사용 가능
 *
 * **예시:**
 *
 * ```ts
 * // 직위 선택 드롭다운: order로 정렬 (사원 → 대표)
 * positionLevels.sort((a, b) => a.order - b.order);
 *
 * // 조직도 구성원 정렬: rankWeight로 정렬 (대표 → 사원)
 * members.sort((a, b) => {
 *   const aWeight = getPositionLevel(a.positionTitle)?.rankWeight || 0;
 *   const bWeight = getPositionLevel(b.positionTitle)?.rankWeight || 0;
 *   return bWeight - aWeight; // 내림차순
 * });
 * ```
 */
